/**
 * Demo Content Modal
 * 
 * Optional modal that appears for new clinics with empty dashboards
 * Offers the choice to load demo content or start fresh
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Award, Users, Calendar, DollarSign, Loader2, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DemoContentModal = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const handleLoadDemo = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/demo/generate`);
      
      toast.success(
        <div>
          <p className="font-semibold">🎉 Demo Content Loaded!</p>
          <p className="text-sm">Your dashboard now has sample data to explore.</p>
        </div>
      );
      
      if (onSuccess) {
        onSuccess(response.data);
      }
      
      // Reload page to show new data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Failed to load demo content:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to load demo content';
      toast.error(errorMsg);
      setLoading(false);
    }
  };

  const handleStartFresh = () => {
    // Just close modal and continue with empty dashboard
    if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto sm:max-h-none">
        {/* Mobile close button - always visible */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-2 bg-gray-100 hover:bg-gray-200 transition-colors z-50 sm:hidden"
          aria-label="Cerrar"
        >
          <X className="h-5 w-5 text-gray-600" />
        </button>
        
        <DialogHeader className="pr-12 sm:pr-0">
          <DialogTitle className="text-xl sm:text-2xl flex items-center gap-2">
            <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
            Welcome to Your Dashboard!
          </DialogTitle>
          <DialogDescription className="text-sm">
            Would you like to explore with demo content or start fresh?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-6 py-2 sm:py-4">
          {/* Demo Content Option */}
          <Card className="border-2 border-purple-200 hover:border-purple-400 transition-colors cursor-pointer group">
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-2 sm:p-3 rounded-lg group-hover:from-purple-200 group-hover:to-pink-200 transition-colors">
                  <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
                </div>
                <div className="flex-1 w-full">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                    Load Demo Content
                  </h3>
                  <p className="text-sm text-gray-600 mb-3 sm:mb-4">
                    Start exploring immediately with realistic sample data.
                  </p>
                  
                  {/* What's Included - Compact on mobile */}
                  <div className="bg-purple-50 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
                    <p className="font-semibold text-purple-900 mb-2 sm:mb-3 text-sm">What's included:</p>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                        <Award className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600 flex-shrink-0" />
                        <span className="text-purple-800">3 Plans</span>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                        <Users className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600 flex-shrink-0" />
                        <span className="text-purple-800">12 Patients</span>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                        <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600 flex-shrink-0" />
                        <span className="text-purple-800">30+ Appts</span>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                        <DollarSign className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600 flex-shrink-0" />
                        <span className="text-purple-800">15+ Payments</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleLoadDemo}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-sm sm:text-base"
                    size="default"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                        Load Demo Content
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-purple-600 mt-2 text-center hidden sm:block">
                    ℹ️ You can clear demo data anytime from Settings
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Start Fresh Option - Simplified on mobile */}
          <Card className="border-2 border-gray-200 hover:border-gray-400 transition-colors cursor-pointer group">
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                <div className="bg-gray-100 p-2 sm:p-3 rounded-lg group-hover:bg-gray-200 transition-colors">
                  <Award className="h-6 w-6 sm:h-8 sm:w-8 text-gray-600" />
                </div>
                <div className="flex-1 w-full">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                    Start Fresh
                  </h3>
                  <p className="text-sm text-gray-600 mb-3 sm:mb-4">
                    Begin with a clean slate. Create your own plans and add real patients.
                  </p>
                  
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4 hidden sm:block">
                    <p className="text-sm text-gray-700">
                      <strong>Best for:</strong> Experienced users who want full control.
                    </p>
                  </div>

                  <Button
                    onClick={handleStartFresh}
                    disabled={loading}
                    variant="outline"
                    className="w-full border-2 text-sm sm:text-base"
                    size="default"
                  >
                    Start Fresh
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="text-center text-xs sm:text-sm text-gray-500 hidden sm:flex">
          <p className="w-full">
            You can always access this option later from the Help menu
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DemoContentModal;
